import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClockIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { adminService, type Tenant } from '../services/admin'
import { PlatformHealth } from '../components/PlatformHealth'
import { TrialPipeline } from './home/TrialPipeline'
import { usePageTitle } from '../lib/pageTitle'
import { sectorPath, useSector } from '../sector/sector'
import { PageHeader } from '../components/ui/PageHeader'
import { StatTile } from '../components/ui/StatTile'

function TenantRow({ tenant, isOnboarding }: { tenant: Tenant; isOnboarding: boolean }) {
  const status = isOnboarding ? 'onboarding' : tenant.is_active ? 'active' : 'inactive'
  const statusConfig = {
    active: { label: 'Active', class: 'bg-success-100 text-success-700' },
    onboarding: { label: 'Onboarding', class: 'bg-warning-100 text-warning-700' },
    inactive: { label: 'Inactive', class: 'bg-surface-alt text-ink-muted' },
  }

  return (
    <tr className="hover:bg-accent-soft/30 transition-colors">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: (tenant.brand_color || '#006D77') + '20' }}
          >
            <span
              className="font-outfit font-bold"
              style={{ color: tenant.brand_color || '#006D77' }}
            >
              {tenant.short_name?.charAt(0)?.toUpperCase() || tenant.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-inter text-body font-medium text-ink">{tenant.name}</p>
            <p className="font-inter text-caption text-ink-faint">{tenant.short_name}</p>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className={clsx(
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-caption font-medium',
          statusConfig[status].class
        )}>
          <span className={clsx(
            'w-1.5 h-1.5 rounded-full',
            status === 'active' ? 'bg-success' : status === 'onboarding' ? 'bg-warning' : 'bg-ink-faint'
          )} />
          {statusConfig[status].label}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded-card border border-hairline"
            style={{ backgroundColor: tenant.brand_color || '#006D77' }}
          />
          <span className="font-mono text-body-sm text-ink-muted">
            {tenant.brand_color || '-'}
          </span>
        </div>
      </td>
      <td className="px-6 py-4">
        <p className="font-inter text-body-sm text-ink-muted">
          {new Date(tenant.created_at).toLocaleDateString('en-KE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </p>
      </td>
    </tr>
  )
}


type TenantListFilters = {
  status?: 'active' | 'inactive'
  onboarding?: 'pending' | 'stalled' | 'completed' | 'in_progress'
}

function buildTenantListSearch(filters: TenantListFilters): string {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.onboarding) params.set('onboarding', filters.onboarding)
  const query = params.toString()
  return query ? `?${query}` : ''
}

/** A sector's Home: its customers, onboarding and trials at a glance. */
export default function DashboardPage() {
  usePageTitle('Home')
  const navigate = useNavigate()
  const sector = useSector()
  const code = sector.code
  const to = (page: string) => navigate(sectorPath(code, page))

  const {
    data: tenants,
    isLoading: tenantsLoading,
    isError: tenantsError,
  } = useQuery({
    queryKey: ['tenants', code],
    queryFn: () => adminService.listTenants(code),
  })

  const { data: onboardingStats } = useQuery({
    queryKey: ['onboarding', code, 'stats'],
    queryFn: () => adminService.getOnboardingStats(code),
  })

  const {
    data: pendingOnboardings,
    isLoading: pendingLoading,
    isError: pendingError,
  } = useQuery({
    queryKey: ['onboarding', code, 'pending'],
    queryFn: () => adminService.getPendingOnboardings(code),
  })

  const {
    data: stalledOnboardings,
    isLoading: stalledLoading,
    isError: stalledError,
  } = useQuery({
    queryKey: ['onboarding', code, 'stalled'],
    queryFn: () => adminService.getStalledOnboardings(code),
  })

  const navigateToTenantList = (filters: TenantListFilters = {}) => {
    navigate(`${sectorPath(code, 'customers')}${buildTenantListSearch(filters)}`)
  }

  const activeTenants = tenants?.filter(t => t.is_active).length ?? 0
  const pendingTenantIds = new Set(pendingOnboardings?.map(p => p.tenant_id) ?? [])
  const sortedTenants = [...(tenants ?? [])].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title={sector.name}
        subtitle={`What's happening with ${sector.name} customers today`}
        actions={
          <>
            <button
              onClick={() => to('onboarding')}
              className="btn-secondary btn-sm"
            >
              <ClipboardDocumentListIcon className="h-5 w-5" />
              Onboarding Pipeline
            </button>
            <button
              onClick={() => to('onboarding/new')}
              className="btn-primary btn-sm"
            >
              <BuildingOffice2Icon className="h-5 w-5" />
              Onboard Customer
            </button>
          </>
        }
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          title="Total Customers"
          value={tenants?.length ?? 0}
          icon={BuildingOffice2Icon}
          tone="teal"
          mono
          onClick={() => navigateToTenantList()}
          isLoading={tenantsLoading}
          isError={tenantsError}
        />
        <StatTile
          title="Active Customers"
          value={activeTenants}
          icon={CheckCircleIcon}
          tone="mint"
          mono
          onClick={() => navigateToTenantList({ status: 'active' })}
          isLoading={tenantsLoading}
          isError={tenantsError}
        />
        <StatTile
          title="Pending Onboarding"
          value={pendingOnboardings?.length ?? 0}
          icon={ClipboardDocumentListIcon}
          tone="clay"
          mono
          onClick={() => navigateToTenantList({ onboarding: 'pending' })}
          isLoading={pendingLoading}
          isError={pendingError}
        />
        <StatTile
          title="Stalled"
          value={stalledOnboardings?.length ?? 0}
          icon={ExclamationTriangleIcon}
          tone="info"
          mono
          onClick={() => navigateToTenantList({ onboarding: 'stalled' })}
          isLoading={stalledLoading}
          isError={stalledError}
        />
      </div>

      {/* Trial Pipeline */}
      <TrialPipeline sector={code} />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Schools Table - Takes 2 columns */}
        <div className="lg:col-span-2 bg-surface rounded-2xl shadow-card border border-hairline overflow-hidden">
          <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
            <div>
              <h2 className="font-outfit text-h4 text-ink">Customers Overview</h2>
              <p className="font-inter text-body-sm text-ink-muted mt-0.5">
                Every {sector.name} customer
              </p>
            </div>
            <button
              onClick={() => to('customers')}
              className="font-inter text-body-sm text-accent hover:text-accent font-medium"
            >
              View All
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-canvas border-b border-hairline">
                  <th className="px-6 py-3 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">
                    Brand
                  </th>
                  <th className="px-6 py-3 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {tenantsLoading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                        <p className="font-inter text-body-sm text-ink-muted">Loading customers...</p>
                      </div>
                    </td>
                  </tr>
                ) : !tenants?.length ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <BuildingOffice2Icon className="h-12 w-12 text-ink-faint" />
                        <div>
                          <p className="font-outfit text-h4 text-ink">No customers yet</p>
                          <p className="font-inter text-body-sm text-ink-muted mt-1">
                            Get started by onboarding your first customer
                          </p>
                        </div>
                        <button
                          onClick={() => to('onboarding/new')}
                          className="btn-primary mt-2"
                        >
                          Onboard Customer
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedTenants.map((tenant) => (
                    <TenantRow
                      key={tenant.id}
                      tenant={tenant}
                      isOnboarding={pendingTenantIds.has(tenant.id)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Onboarding Summary */}
          <div className="bg-surface rounded-2xl shadow-card border border-hairline p-6">
            <h3 className="font-outfit text-h4 text-ink mb-4">Onboarding Pipeline</h3>
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => navigateToTenantList()}
                className="w-full flex items-center justify-between rounded-card px-2 py-1 hover:bg-accent-soft/60 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-card bg-accent-soft flex items-center justify-center">
                    <ClipboardDocumentListIcon className="h-5 w-5 text-accent" />
                  </div>
                  <span className="font-inter text-body-sm text-ink">Total</span>
                </div>
                <span className="font-outfit text-body font-medium text-ink">
                  {onboardingStats?.total ?? '-'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigateToTenantList({ onboarding: 'in_progress' })}
                className="w-full flex items-center justify-between rounded-card px-2 py-1 hover:bg-accent-soft/60 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-card bg-warning-50 flex items-center justify-center">
                    <ClockIcon className="h-5 w-5 text-warning-600" />
                  </div>
                  <span className="font-inter text-body-sm text-ink">In Progress</span>
                </div>
                <span className="font-outfit text-body font-medium text-ink">
                  {onboardingStats?.in_progress ?? '-'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigateToTenantList({ onboarding: 'pending' })}
                className="w-full flex items-center justify-between rounded-card px-2 py-1 hover:bg-accent-soft/60 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-card bg-info-50 flex items-center justify-center">
                    <ClipboardDocumentListIcon className="h-5 w-5 text-info-600" />
                  </div>
                  <span className="font-inter text-body-sm text-ink">Pending</span>
                </div>
                <span className="font-outfit text-body font-medium text-ink">
                  {pendingOnboardings?.length ?? '-'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigateToTenantList({ onboarding: 'completed' })}
                className="w-full flex items-center justify-between rounded-card px-2 py-1 hover:bg-accent-soft/60 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-card bg-success-100 flex items-center justify-center">
                    <CheckCircleIcon className="h-5 w-5 text-success-600" />
                  </div>
                  <span className="font-inter text-body-sm text-ink">Completed</span>
                </div>
                <span className="font-outfit text-body font-medium text-success-600">
                  {onboardingStats?.completed ?? '-'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigateToTenantList({ onboarding: 'stalled' })}
                className="w-full flex items-center justify-between rounded-card px-2 py-1 hover:bg-accent-soft/60 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-card bg-clay-50 flex items-center justify-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-clay-600" />
                  </div>
                  <span className="font-inter text-body-sm text-ink">Stalled</span>
                </div>
                <span className="font-outfit text-body font-medium text-clay-600">
                  {stalledOnboardings?.length ?? '-'}
                </span>
              </button>
            </div>
            <button
              onClick={() => to('onboarding')}
              className="w-full mt-4 btn-ghost border border-hairline text-accent"
            >
              View Pipeline
            </button>
          </div>

          <PlatformHealth />
        </div>
      </div>
    </div>
  )
}

